import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PlanStep } from '../../types/generated/graphql';
import { ENQUEUE_STEP_EXECUTION } from '../../graphql/mutations';
import { GET_PLAN } from '../../graphql/queries';
import { canExecuteStep } from '../../utils/planHelpers';
import { colors } from '../../theme/colors';
import { useToast } from '../../contexts/ToastContext';

interface DoThisForMeButtonProps {
  /** The plan ID */
  planId: string;
  /** The step to execute */
  step: PlanStep;
  /** Button variant: 'primary' shows contained button, 'secondary' shows text button */
  variant?: 'primary' | 'secondary';
  /** Size of the button */
  size?: 'small' | 'medium' | 'large';
  /** Called after successful execution */
  onSuccess?: () => void;
  /** Called after execution failure */
  onError?: (error: Error) => void;
  /** Pre-computed canExecute value to avoid recalculating (optional) */
  canExecute?: boolean;
}

/**
 * "Do This for Me" button that triggers automation execution for a step.
 * 
 * Only renders for AUTOMATION and MIXED steps that have skills or actions.
 * Shows loading state during execution and handles errors gracefully.
 */
export const DoThisForMeButton: React.FC<DoThisForMeButtonProps> = ({
  planId,
  step,
  variant = 'primary',
  size = 'medium',
  onSuccess,
  onError,
  canExecute: canExecuteProp,
}) => {
  const [showError, setShowError] = useState(false);
  const { showToast } = useToast();

  const [enqueueStep, { loading, error }] = useMutation(ENQUEUE_STEP_EXECUTION, {
    variables: { planId, stepId: step.id, cascade: true },
    // Refetch plan after a short delay to pick up status changes
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: false, // Don't wait - job runs in background
    onCompleted: (data) => {
      if (data?.enqueueStepExecution?.jobId) {
        setShowError(false);
        showToast({
          message: data.enqueueStepExecution.message || 'Step queued for execution',
          severity: 'success',
          autoHideDuration: 4000,
        });
        onSuccess?.();
        // Refetch plan after a delay to pick up worker updates
        setTimeout(() => {
          // The refetchQueries will handle this
        }, 2000);
      }
    },
    onError: (err) => {
      setShowError(true);
      showToast({
        message: err.message || 'Failed to queue step. Please try again.',
        severity: 'error',
      });
      onError?.(err);
    },
  });

  // Use provided canExecute prop if available, otherwise calculate it
  // This avoids recalculating when parent component has already computed it
  const canExecute = canExecuteProp !== undefined ? canExecuteProp : canExecuteStep(step);

  // Don't render if step cannot be executed
  if (!canExecute) {
    return null;
  }

  const handleClick = () => {
    setShowError(false);
    enqueueStep();
  };

  // Determine button state
  const hasError = !!error || showError;
  const buttonText = loading
    ? 'Working on it...'
    : hasError
      ? 'Try Again'
      : 'Do This for Me';

  const buttonIcon = loading ? (
    <CircularProgress size={16} color="inherit" />
  ) : hasError ? (
    <RefreshIcon sx={{ fontSize: 18 }} />
  ) : (
    <SmartToyIcon sx={{ fontSize: 18 }} />
  );

  const isPrimary = variant === 'primary';

  return (
    <>
      <Tooltip
        title={loading ? 'Planapus is working on this step...' : 'Let Planapus handle this step automatically'}
        arrow
        placement="top"
      >
        <Button
          variant={isPrimary ? 'contained' : 'text'}
          size={size}
          onClick={handleClick}
          disabled={loading}
          startIcon={buttonIcon}
          sx={{
            ...(isPrimary && !hasError && {
              bgcolor: colors.green[600],
              '&:hover': {
                bgcolor: colors.green[700],
              },
            }),
            ...(isPrimary && hasError && {
              bgcolor: colors.amber[600],
              '&:hover': {
                bgcolor: colors.amber[700],
              },
            }),
            ...(!isPrimary && {
              color: colors.green[600],
              '&:hover': {
                bgcolor: `${colors.green[600]}10`,
              },
            }),
          }}
        >
          {buttonText}
        </Button>
      </Tooltip>
    </>
  );
};

export default DoThisForMeButton;

